/**
 * SessionDB stores `time.time()` (Unix **seconds** as float). Client-only paths
 * often use `Date.now()` (**milliseconds**). Normalize so Date displays correctly.
 */
export function toJavaScriptMs(raw: number | null | undefined): number {
  if (raw == null || !Number.isFinite(raw)) return Date.now();
  return raw < 1e12 ? raw * 1000 : raw;
}

export function formatMs(ms?: number | null): string {
  if (ms == null || ms === undefined) return "n/a";
  if (!Number.isFinite(ms)) return "n/a";
  return new Date(toJavaScriptMs(ms)).toLocaleString();
}

export function formatDurationHuman(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remSec = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remSec}s`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return `${hours}h ${remMin}m`;
}

export function formatRelativeTimestamp(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  const t = toJavaScriptMs(ms);
  const now = Date.now();
  const diff = now - t;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(t).toLocaleDateString();
}

export function clampText(value: string, max = 120): string {
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(0, max - 1))}…`;
}

export function truncateText(value: string, max: number): { text: string; truncated: boolean; total: number } {
  if (value.length <= max) return { text: value, truncated: false, total: value.length };
  return { text: value.slice(0, Math.max(0, max)), truncated: true, total: value.length };
}

export function stripThinkingTags(value: string): string {
  return value.replace(/<thinking>[\s\S]*?<\/thinking>/gi, "").trim();
}

export function formatCost(cost: number | null | undefined, fallback = "$0.00"): string {
  if (cost == null || !Number.isFinite(cost)) return fallback;
  if (cost === 0) return "$0.00";
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  if (cost < 1) return `$${cost.toFixed(3)}`;
  return `$${cost.toFixed(2)}`;
}

export function formatTokens(tokens: number | null | undefined, fallback = "0"): string {
  if (tokens == null || !Number.isFinite(tokens)) return fallback;
  if (tokens < 1000) return String(Math.round(tokens));
  if (tokens < 1_000_000) {
    const k = tokens / 1000;
    return k < 10 ? `${k.toFixed(1)}k` : `${Math.round(k)}k`;
  }
  const m = tokens / 1_000_000;
  return m < 10 ? `${m.toFixed(1)}M` : `${Math.round(m)}M`;
}

export function formatPercent(value: number | null | undefined, fallback = "—"): string {
  if (value == null || !Number.isFinite(value)) return fallback;
  return `${(value * 100).toFixed(1)}%`;
}

export function formatList(values?: Array<string | null | undefined>): string {
  if (!values || values.length === 0) return "none";
  return values.filter((v): v is string => Boolean(v && v.trim())).join(", ");
}

export function parseList(input: string): string[] {
  return input
    .split(/[,\n]/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}
